import {
  HttpErrorResponse,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { NewrelicService } from 'src/app/analytics/newrelic/services/newrelic.service';
import { SignOutService } from '../shared-components/exit-app-and-sign-out/sign-out/sign-out.service';
import { AuthService } from '../services/auth/auth.service';
import { RefreshService } from '../services/evergent/login/refresh.service';
import { ConfigService } from '../services/config.service';
import { KEYS } from '../utils/constants/storage.constants';
import { DataService } from 'src/app/data.service';

@Injectable()
export class HttpGeneralInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private readonly signOutService: SignOutService,
    private readonly newrelic: NewrelicService,
    private readonly authService: AuthService,
    private readonly refreshService: RefreshService,
    private readonly configService: ConfigService,
    private readonly dataService: DataService
  ) {}
  // Need to Implement Typings
  intercept(req: any, next: any): Observable<any> {
    try {
      const auth = req.headers.get('authorization');
      if (auth !== null && auth !== undefined) {
        if (
          auth.trim().toUpperCase() === 'BEARER' ||
          auth.trim().toUpperCase() === 'BEARER UNDEFINED' ||
          auth.trim().toUpperCase() === 'BEARER NULL'
        ) {
          return next.handle(req); // Don't return null, continue the request
        }
      }
    } catch (e) {
      console.warn(e);
    }

    // Proactive Refresh: Check if token is about to expire
    const isRefreshApi = req.url.includes('/refreshToken');
    if (
      !isRefreshApi &&
      this.authService.isLoggedIn() &&
      this.authService.isTokenNearlyExpired() &&
      !this.dataService.isLoggingOut
    ) {
      return this.handleRefreshToken(req, next);
    }

    return next.handle(req).pipe(
      switchMap((event: any) => {
        if (event instanceof HttpResponse) {
          if (event.status === 500) {
            this.handle500Error(event);
          }

          this.getResponseCode(event.body);

          const errorCode = this.extractErrorCode(event.body);
          if (
            !isRefreshApi &&
            errorCode &&
            ['eV2124', 'eV2767', '111111111'].includes(errorCode)
          ) {
            return this.handleRefreshToken(req, next);
          }
        }
        return of(event);
      }),
      catchError((error: HttpErrorResponse) => {
        // Reactive Refresh: Handle specific "Expired Token" error codes
        if (
          !isRefreshApi &&
          error instanceof HttpErrorResponse &&
          (error.status === 401 || this.isTokenExpiredError(error)) &&
          !this.dataService.isLoggingOut
        ) {
          return this.handleRefreshToken(req, next);
        }
        this.handleHttpError(error, req);
        return throwError(() => error);
      })
    );
  }

  private handle500Error(event: HttpResponse<any>): void {
    this.newrelic.sendErrorEvent({
      error: {
        errorCode: '500',
        errorMessage: 'Internal Server Error',
        errorDescription:
          'Our systems are currently experiencing high traffic. Please try again in an hour.',
      },
    });
  }

  private isTokenExpiredError(error: any): boolean {
    const errorCode = this.extractErrorCode(error?.error || error?.body);
    return ['eV2124', 'eV2767', '111111111'].includes(errorCode || '');
  }

  private extractErrorData(response: any): {
    errorCode: string | null;
    errorMessage: string | null;
  } {
    if (!response) return { errorCode: null, errorMessage: null };
    const responseKeys = Object.keys(response);
    const failureMessage =
      responseKeys?.length === 1 && response[responseKeys[0]]?.failureMessage
        ? response[responseKeys[0]]?.failureMessage
        : null;
    return {
      errorCode: failureMessage?.[0]?.errorCode || null,
      errorMessage: failureMessage?.[0]?.errorMessage || null,
    };
  }

  private extractErrorCode(response: any): string | null {
    return this.extractErrorData(response).errorCode;
  }

  private handleRefreshToken(req: any, next: any): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const appConfig = this.configService.applicationConfig;
      return from(this.refreshService.makeRefreshTokenCall(appConfig)).pipe(
        tap(() => {
          this.isRefreshing = false;
          const newToken = localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
          this.refreshTokenSubject.next(newToken);
        }),
        switchMap(() => {
          const newToken = localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
          return this.addToken(req, next, newToken);
        }),
        catchError((err) => {
          this.isRefreshing = false;
          // Notify other waiting requests that the refresh failed
          this.refreshTokenSubject.error(err);
          // Reset subject for future attempts
          this.refreshTokenSubject = new BehaviorSubject<string | null>(null);

          this.signOutService.forceLogout();
          return throwError(() => err);
        })
      );
    } else {
      // Wait for the ongoing refresh
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return this.addToken(req, next, token);
        })
      );
    }
  }

  private addToken(req: any, next: any, token: string | null): Observable<any> {
    if (!token) return throwError(() => new Error('No token available'));

    // Some APIs might need the token in headers, adjust as needed
    const clonedReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    // For Evergent APIs, the token might be in the request body (e.g., RefreshTokenRequestModel)
    // However, the interceptor usually handles header-based auth.
    // If the body needs updating, that's more complex.
    return next.handle(clonedReq);
  }

  private handleHttpError(error: HttpErrorResponse, request: any): void {
    this.newrelic.sendErrorEvent({
      error: {
        errorCode: error.status?.toString() || 'UNKNOWN',
        errorMessage: error.message || 'HTTP Error',
        errorDescription: error.error?.message || error.statusText || 'UNKNOWN',
      },
    });
  }

  private getResponseCode(response: any): void {
    const errorData = this.extractErrorData(response);
    if (errorData.errorCode) {
      this.newrelic.sendErrorEvent({
        error: {
          errorCode: errorData.errorCode,
          errorMessage: errorData.errorMessage || 'API Error Body',
          errorDescription: `The API returned an error code in the body: ${errorData.errorCode}${
            errorData.errorMessage ? ` - ${errorData.errorMessage}` : ''
          }`,
        },
      });
    }
  }
}
